import { addDays, format, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { Order } from '@/types/order'

export function generateShipmentCalendarEvent(brandLabel: string, order: Order): string {
  // Start with the calendar header
  const calendarHeader = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${brandLabel}//Shipment Calendar//EN`,
  ]

  // Generate all-day events for each leg (ship date and arrival date)
  const events = order.legs
    .flatMap((leg): string[] => {
      const legName = leg.leg_name
      const origin = `${leg.origin.address_1}, ${leg.origin.city}, ${leg.origin.state}`

      // Get the first shipment's dates for this leg
      const shipment = leg.travelers[0]?.shipments[0]
      if (!shipment) return []

      const shipDate = new Date(shipment.ship_date)

      // Format as all-day event (ICS spec: DTEND is exclusive, so add 1 day)
      const formatAllDay = (date: Date): string => format(date, 'yyyyMMdd')
      const formatAllDayEnd = (date: Date): string => format(addDays(date, 1), 'yyyyMMdd')

      const events: string[] = []

      const isPickup = leg.pickup !== false
      const carrierName = shipment.carrier_service_level?.carrier?.display_name || 'Carrier'

      const eventType = isPickup ? 'Pickup' : 'Drop off'
      const eventDescription = `${legName} shipment ${isPickup ? 'pickup' : 'drop off'}`
      const eventLocation = isPickup ? origin : `${carrierName} Drop-Off Location`

      const calendarEvent = [
        'BEGIN:VEVENT',
        `DTSTART;VALUE=DATE:${formatAllDay(shipDate)}`,
        `DTEND;VALUE=DATE:${formatAllDayEnd(shipDate)}`,
        `SUMMARY:${brandLabel} ${eventType}`,
        `DESCRIPTION:${eventDescription}`,
        `LOCATION:${eventLocation}`,
        'END:VEVENT',
      ].join('\r\n')

      events.push(calendarEvent)

      return events
    })
    .filter(Boolean)

  // Combine header, events, and footer
  const icsContent = [...calendarHeader, ...events, 'END:VCALENDAR'].join('\r\n')

  return icsContent
}

// The pickup-ready ISO is the backend slot time serialized in the pickup
// location's zone, so its trailing numeric offset (e.g. "-05:00") encodes the
// correct local wall clock even when no IANA zone is available. "Z"/absent
// carries no location information, so it doesn't count.
const utcOffsetOf = (iso: string): string | undefined => iso.match(/[+-]\d{2}:\d{2}$/)?.[0]

export function formatUberPickupWindow(
  pickupReadyTime: string,
  pickupCloseTime: string,
  timeZone?: string,
): string {
  // Fall back to the ready time's embedded offset for both times — the close
  // time is re-derived via toISOString() upstream, so it's UTC and would
  // otherwise render in the browser's zone.
  const zone = timeZone || utcOffsetOf(pickupReadyTime)
  const fmt = (iso: string, pattern: string): string =>
    zone ? formatInTimeZone(iso, zone, pattern) : format(parseISO(iso), pattern)

  const day = fmt(pickupReadyTime, 'MMMM do')
  const startTime = fmt(pickupReadyTime, 'h')
  const endTime = fmt(pickupCloseTime, 'haaa')

  // If start time has a different am/pm than end time, include it
  const startAmPm = fmt(pickupReadyTime, 'aaa')
  const endAmPm = fmt(pickupCloseTime, 'aaa')
  const startFormatted = startAmPm === endAmPm ? startTime : `${startTime}${startAmPm}`

  return `On ${day} at ${startFormatted}-${endTime}`
}

export function generateUberPickupCalendarEvent(brandLabel: string, order: Order): string {
  const leg = order.legs[0]
  if (!leg?.pickup_ready_time || !leg?.pickup_close_time) {
    return generateShipmentCalendarEvent(brandLabel, order)
  }

  const timeZone = leg.origin?.time_zone
  const localPattern = "yyyyMMdd'T'HHmmss"
  const utcPattern = "yyyyMMdd'T'HHmmss'Z'"
  // Without an IANA zone we can't emit TZID, but when the ready ISO carries an
  // offset the instant is trustworthy — emit UTC form rather than a floating
  // local time, which lands at the wrong instant for cross-zone viewers.
  const hasOffset = !!utcOffsetOf(leg.pickup_ready_time)
  const stamp = (iso: string): string =>
    hasOffset ? formatInTimeZone(iso, 'UTC', utcPattern) : format(parseISO(iso), localPattern)
  const dtstart = timeZone
    ? `DTSTART;TZID=${timeZone}:${formatInTimeZone(leg.pickup_ready_time, timeZone, localPattern)}`
    : `DTSTART:${stamp(leg.pickup_ready_time)}`
  const dtend = timeZone
    ? `DTEND;TZID=${timeZone}:${formatInTimeZone(leg.pickup_close_time, timeZone, localPattern)}`
    : `DTEND:${stamp(leg.pickup_close_time)}`

  const origin = `${leg.origin.address_1}, ${leg.origin.city}, ${leg.origin.state}`

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${brandLabel}//Uber Pickup Calendar//EN`,
    'BEGIN:VEVENT',
    dtstart,
    dtend,
    `SUMMARY:${brandLabel} Uber Pickup`,
    `DESCRIPTION:Uber will pick up your bags`,
    `LOCATION:${origin}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n')

  return icsContent
}

export function downloadCalendarEvent(
  brandName: string,
  icsContent: string,
  orderId: string,
): void {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
  const link = document.createElement('a')

  link.href = URL.createObjectURL(blob)
  link.download = `${brandName}-shipment-${orderId}.ics`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(link.href)
}
